/**
 * 메뉴 접근 판정 — 프론트엔드 **정본**.
 *
 * ⛔ 이 파일 밖에서 menu_permissions 를 직접 해석하지 않는다.
 *
 * 왜 한 곳으로 모았나
 * -------------------
 * 2026-08-19 전수조사에서 같은 `users.menu_permissions` 컬럼을 **네 곳이 서로 다르게** 읽고 있었다.
 *
 *   ① src/middlewares/auth.js:326-333   role 통과 O · null=허용   (백엔드 라우트 가드)
 *   ② frontend routes/optimized.tsx     role 통과 **X** · null=허용 (프론트 라우트 가드)
 *   ③ src/services/mcp/mcp-permissions.js  role 통과 X · null=**거부**(fail-closed)
 *   ④ src/utils/hr-access.js            superadmin 만 · null=거부(opt-in)
 *
 * ②에 role 통과 분기가 없는 것이 「superadmin/admin 에 부분 권한을 넣으면 ERP 프론트가 잠긴다」의
 * 근본 원인이었다 — 백엔드는 통과시키는데 화면이 스스로 막았다. 이 파일은 ①과 판정을 일치시킨다.
 *
 * ③·④는 의도적으로 다르다(MCP 는 fail-closed, 퇴사자 열람은 opt-in). 통합 대상이 아니다.
 */

import { normalizePermissions, type MenuPermissions } from '../services/adminService';
import { PERMISSION_KEY_ALIASES } from '../constants/menuData';

/** 메뉴 권한을 무시하고 모든 화면을 통과하는 역할 — 백엔드 auth.js:326 과 동일 목록 */
const FULL_ACCESS_ROLES = ['admin', 'superadmin'] as const;

export type MenuAction = 'view' | 'edit' | 'delete';

export interface MenuAccessSubject {
  role?: string | null;
  menu_permissions?: MenuPermissions;
}

/**
 * 이 역할이 메뉴 권한 검사를 건너뛰는가.
 *
 * 관리자에게 부분 권한이 저장돼 있어도 통과시킨다 — 저장 자체를 백엔드가 막지만(admin.js),
 * 이미 저장된 과거 값이나 역할 승격으로 남은 값 때문에 관리자가 자기 화면에서 잠기면 안 된다.
 */
export const hasFullMenuAccess = (role?: string | null): boolean =>
  !!role && (FULL_ACCESS_ROLES as readonly string[]).includes(role);

/**
 * 메뉴 하나에 대한 접근 판정.
 *
 * 판정 순서 — 백엔드 auth.js requireMenuAccess 와 동일하다.
 *   1. admin/superadmin 이면 통과
 *   2. menu_permissions 가 null/undefined 면 통과 (전체 허용이 기본값)
 *   3. 배열(레거시)이면 view 만 인정
 *   4. Record 면 해당 키의 action 이 true 인지 확인. 키가 없으면 **거부**
 */
export const canAccessMenu = (
  subject: MenuAccessSubject | null | undefined,
  menuKey: string,
  action: MenuAction = 'view',
): boolean => {
  // 사용자 정보가 아직 없을 때는 **막지 않는다**.
  // 로그인 여부는 ProtectedRoute 가 이미 본다. 여기서 false 를 돌려주면
  // /auth/me 가 일시 실패하거나(429 — 전역 300회/분 한도) 스토리지가 비었을 때
  // 토큰이 멀쩡한데도 모든 화면이 «접근 거부» 로 튕긴다. 종전 판정도 통과였다.
  if (!subject) return true;

  if (hasFullMenuAccess(subject.role)) return true;

  // 다른 메뉴의 권한을 따르는 항목(menuData permissionKey) — 예: 평생수선 → 클레임
  menuKey = PERMISSION_KEY_ALIASES[menuKey] ?? menuKey;

  const perms = subject.menu_permissions;
  if (!perms) return true;

  if (Array.isArray(perms)) {
    // 레거시 배열은 view 만 인정한다 (백엔드 auth.js:337 [C-18] 과 동일)
    return action === 'view' && perms.includes(menuKey);
  }

  const normalized = normalizePermissions(perms);
  if (!normalized) return true;

  return normalized[menuKey]?.[action] === true;
};
